// @ts-nocheck

import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '@/store';
import {
  GetStudyYearCompanyRequest,
  GetStudyYearCompaniesRequest,
  UpdateStudyYearCompanyRequest,
  UploadSurveyRequest,
  DownloadSurveyRequest,
  StudyYearCompanyDetail,
  StudyYearCompanyReadSchema,
  StudyYearCompanySchema,
  SYCategoryCompanySchema,
  PersonSchema,
  StudyCompanyHistory,
  GetStudyYearCompanyHistoryRequest,
} from '@/types/study-year-companies';
import { studyYearsCompaniesService } from '../../services/study-year-companies.service';
import { showSnackbar } from '../snakebar/slice';

export const studyYearCompanies = {
  /**
   * Get all study year companies with filtering
   * GET /v3/study-year-companies/
   */
  getStudyYearCompanies: createAsyncThunk<
    StudyYearCompanyReadSchema[],
    GetStudyYearCompaniesRequest | undefined
  >('studyYearCompanies/getStudyYearCompanies', async (params, { rejectWithValue }) => {
    try {
      const response = await studyYearsCompaniesService.getStudyYearCompanies(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch study year companies'
      );
    }
  }),

  /**
   * Get study year company by ID
   * GET /v3/study-year-companies/{study_year_company_id}
   */
  getStudyYearCompany: createAsyncThunk<StudyYearCompanyDetail, GetStudyYearCompanyRequest>(
    'studyYearCompanies/getStudyYearCompany',
    async (request, { rejectWithValue }) => {
      try {
        const response = await studyYearsCompaniesService.getStudyYearCompany(request);
        return response;
      } catch (error: any) {
        return rejectWithValue(
          error.response?.data?.message ||
            `Failed to fetch study year company ${request.studyYearCompanyId}`
        );
      }
    }
  ),

  /**
   * Update study year company
   * PATCH /v3/study-year-companies/{study_year_company_id}
   */
  updateStudyYearCompany: createAsyncThunk<
    StudyYearCompanyDetail,
    UpdateStudyYearCompanyRequest,
    { dispatch: AppDispatch; state: RootState }
  >('studyYearCompanies/updateStudyYearCompany', async (request, { dispatch, rejectWithValue }) => {
    try {
      const response = await studyYearsCompaniesService.updateStudyYearCompany(request);
      dispatch(
        showSnackbar({
          message: 'Study year company updated successfully!',
          variant: 'success',
        })
      );
      return response;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error instanceof Error ? error.message : 'Failed to update company',
          variant: 'error',
        })
      );
      return rejectWithValue(
        error.response?.data?.message ||
          `Failed to update study year company ${request.studyYearCompanyId}`
      );
    }
  }),

  /**
   * Get study year company segments
   * GET /v3/study-year-companies/{study_year_company_id}/segments
   */
  getStudyYearCompanySegments: createAsyncThunk<
    SYCategoryCompanySchema[],
    GetStudyYearCompanyRequest
  >('studyYearCompanies/getStudyYearCompanySegments', async (request, { rejectWithValue }) => {
    try {
      const response = await studyYearsCompaniesService.getStudyYearCompanySegments(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          `Failed to fetch segments for company ${request.studyYearCompanyId}`
      );
    }
  }),

  /**
   * Download survey for study year company
   * POST /v3/study-year-companies/{study_year_company_id}/download-survey
   */
  downloadSurvey: createAsyncThunk<
    Blob,
    DownloadSurveyRequest,
    { dispatch: AppDispatch; state: RootState }
  >('studyYearCompanies/downloadSurvey', async (request, { dispatch, rejectWithValue }) => {
    try {
      const response = await studyYearsCompaniesService.downloadSurvey(request);
      dispatch(
        showSnackbar({
          message: 'Survey downloaded successfully!',
          variant: 'success',
        })
      );
      return response;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error instanceof Error ? error.message : 'Failed to download survey',
          variant: 'error',
        })
      );
      return rejectWithValue(
        error.response?.data?.message ||
          `Failed to download survey for company ${request.studyYearCompanyId}`
      );
    }
  }),

  /**
   * Upload survey for study year company
   * POST /v3/study-year-companies/{study_year_company_id}/upload-survey
   */
  uploadSurvey: createAsyncThunk<
    object,
    UploadSurveyRequest,
    { dispatch: AppDispatch; state: RootState }
  >('studyYearCompanies/uploadSurvey', async (request, { dispatch, rejectWithValue }) => {
    try {
      const response = await studyYearsCompaniesService.uploadSurvey(request);
      dispatch(
        showSnackbar({
          message: 'Survey uploaded successfully!',
          variant: 'success',
        })
      );
      return response;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message: error instanceof Error ? error.message : 'Failed to upload survey',
          variant: 'error',
        })
      );
      return rejectWithValue(
        error.response?.data?.message ||
          `Failed to upload survey for company ${request.studyYearCompanyId}`
      );
    }
  }),

  /**
   * Get study year company contacts
   * GET /v3/study-year-companies/{study_year_company_id}/contacts
   */
  getStudyYearCompanyContacts: createAsyncThunk<PersonSchema[], GetStudyYearCompanyRequest>(
    'studyYearCompanies/getStudyYearCompanyContacts',
    async (request, { rejectWithValue }) => {
      try {
        const response = await studyYearsCompaniesService.getStudyYearCompanyContacts(request);
        return response;
      } catch (error: any) {
        return rejectWithValue(
          error.response?.data?.message ||
            `Failed to fetch contacts for company ${request.studyYearCompanyId}`
        );
      }
    }
  ),

  /**
   * Get study year company versions
   * GET /v3/study-year-companies/{study_year_company_id}/versions
   */
  getStudyYearCompanyVersions: createAsyncThunk<object[], GetStudyYearCompanyRequest>(
    'studyYearCompanies/getStudyYearCompanyVersions',
    async (request, { rejectWithValue }) => {
      try {
        const response = await studyYearsCompaniesService.getStudyYearCompanyVersions(request);
        return response;
      } catch (error: any) {
        return rejectWithValue(
          error.response?.data?.message ||
            `Failed to fetch versions for company ${request.studyYearCompanyId}`
        );
      }
    }
  ),

  /**
   * Get study year company history
   * GET /v3/study-year-companies/{study_year_company_id}/history
   */
  getStudyYearCompanyHistory: createAsyncThunk<
    StudyCompanyHistory[],
    GetStudyYearCompanyHistoryRequest
  >('studyYearCompanies/getStudyYearCompanyHistory', async (request, { rejectWithValue }) => {
    try {
      const response = await studyYearsCompaniesService.getStudyYearCompanyHistory(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          `Failed to fetch history for company ${request.studyYearCompanyId}`
      );
    }
  }),
};
